import {
  knownRegistrarKind,
  type MovementDirection,
  type RegistrarKind,
  type StockMovementReason,
} from '@simsm/shared-types';
import { apiClient } from '../../app/api-client';

export interface DraftRowValidationError {
  field: string;
  code: string;
  message: string;
}

/** A cell an import could not use, kept with the text the file held (spec 054 research R11). */
export interface DraftRowSourceIssue {
  field: string;
  code: string;
  raw: string;
}

/**
 * The default reason of each direction, remembered by the draft (research R4),
 * and the direction a new movement starts from (docs/shape-ops-default-reasons.md
 * §Decision 3). What nobody has set yet is absent, as in the shared schema; the
 * wire answers it as ''.
 */
export interface DraftDefaultReasons {
  increase?: StockMovementReason;
  decrease?: StockMovementReason;
  direction?: MovementDirection;
}

export interface DraftRow {
  clientRowId: string;
  medicineCode?: string;
  /** Human-readable label captured at pick-time, persisted so reload shows DCI not UUID. */
  medicineLabel?: string;
  lot?: string;
  lotExpiry?: string;
  currentQuantity?: number;
  reason?: string;
  comment?: string;
  correctionOf?: string;
  lotMatch?: 'NEW' | 'EXISTING' | 'UNKNOWN';
  // The three below are the server's. Optional because a row the browser has
  // just built carries none of them until the draft answers.
  /**
   * Stock at the draft's location for this medicine and lot before this row,
   * counting earlier rows of the same draft (research R1, R2).
   */
  previousQuantity?: number;
  /**
   * Latest expiry of this lot at the locations the caller reaches, `YYYY-MM-DD`
   * (R2, CF-43): the whole Registrar for its Administrator, the associated
   * locations for an Operator.
   */
  knownExpiry?: string | null;
  /** 'DAY' | 'MONTH': how `knownExpiry` was reported; it decides how the date is shown. */
  knownExpiryPrecision?: string | null;
  sourceIssues?: DraftRowSourceIssue[];
  validationErrors: DraftRowValidationError[];
}

export interface DraftState {
  id: string;
  operatorIdnp: string;
  registrarId: string;
  siteId: string;
  rows: DraftRow[];
  version: number;
  createdAtIso: string;
  updatedAtIso: string;
  /** Who started the draft, written once at creation (R7). */
  operatorName?: string;
  defaultReasons?: DraftDefaultReasons | null;
}

export interface SubmissionResponse {
  submissionId: string;
  /** The RPT number the save allocated; what the toast and the home band name (R8). */
  reportNumber: string;
  acceptedRows: number;
  receivedAtIso: string;
}

export async function createOrGetDraft(siteId?: string | null): Promise<DraftState> {
  const { data } = await apiClient.post<DraftState>('/stock/drafts', { siteId: siteId ?? null });
  return data;
}

export async function getDraft(id: string): Promise<DraftState> {
  const { data } = await apiClient.get<DraftState>(`/stock/drafts/${id}`);
  return data;
}

/**
 * Starts the report's draft with its first rows (spec 054 FR-023). The bff
 * answers 201 with a new draft, or 200 with the location's open draft, whoever
 * started it (CF-03), with these rows appended to it: either way, the draft to
 * work on.
 */
export async function createDraftWithRows(siteId: string, rows: DraftRow[]): Promise<DraftState> {
  const { data } = await apiClient.post<DraftState>('/stock/drafts', { siteId, rows });
  return data;
}

// No parameters: callers hand it to TanStack Query as the `queryFn` itself.
export async function listDrafts(): Promise<{ drafts: DraftState[] }> {
  const { data } = await apiClient.get<{ drafts: DraftState[] }>('/stock/drafts');
  return data;
}

/** One location's draft (CF-03: the location's, not its author's); the bff leaves out drafts with no rows. */
export async function listSiteDrafts(siteId: string): Promise<{ drafts: DraftState[] }> {
  const { data } = await apiClient.get<{ drafts: DraftState[] }>('/stock/drafts', {
    params: { siteId },
  });
  return data;
}

/**
 * The location's open draft, whoever started it (CF-03), or the one bound to no location
 * when `siteId` is null (drafts started before spec 054, or by the import);
 * null when there is none. Filtered here as well as by the bff, so a bff that
 * does not know the `siteId` filter yet cannot hand another location's rows to
 * this one.
 */
export async function findOpenDraft(siteId: string | null): Promise<DraftState | null> {
  const { drafts } = siteId ? await listSiteDrafts(siteId) : await listDrafts();
  if (siteId) return drafts.find((draft) => draft.siteId === siteId) ?? null;
  return drafts.find((draft) => !draft.siteId && draft.rows.length > 0) ?? null;
}

/**
 * The open drafts the session reaches, as the drafts count and the Acasă band
 * read them: every draft of the Registrar for its Administrator, those of its
 * locations for an Operator (CF-03).
 * A draft with no rows is not a report anybody started (FR-023, research R7):
 * the bff already leaves it out, and this does too, as a safety net.
 */
export async function listOpenDrafts(): Promise<DraftState[]> {
  const { drafts } = await listDrafts();
  return drafts.filter((draft) => draft.rows.length > 0);
}

/** A confirmed movement, as correction mode pre-fills it (research R9). */
export interface MovementForCorrection {
  /** The `core.stock` row id; what the correction row's `correctionOf` names. */
  id: string;
  medicineCode: string;
  medicineLabel: string;
  lot: string;
  /** `YYYY-MM-DD`. */
  lotExpiry: string;
  /** '' for a movement filed under no location, which this flow cannot correct. */
  siteId: string;
  /**
   * The movement's quantity as it stands: the level it recorded, as any correction of it since has
   * replaced it (CF-59). "Raportat: X", and what a correction is measured from.
   */
  reportedLevel: number;
  confirmedAt: string;
  windowDays: number;
  withinWindow: boolean;
  /** The channel of the report holding it, `MANUAL` or `AUTOMATED` (UC91-16, CF-59). */
  source: string;
  reportNumber: string;
}

/** `GET /stock/movements/:id`; a missing and a foreign movement answer the same 404. */
export async function getMovement(id: string): Promise<MovementForCorrection> {
  const { data } = await apiClient.get<MovementForCorrection>(
    `/stock/movements/${encodeURIComponent(id)}`,
  );
  return data;
}

export async function upsertDraft(
  id: string,
  expectedVersion: number,
  rows: DraftRow[],
  siteId?: string | null,
  defaultReasons?: DraftDefaultReasons,
): Promise<DraftState> {
  const { data } = await apiClient.patch<DraftState>(`/stock/drafts/${id}`, {
    expectedVersion,
    rows,
    siteId: siteId ?? undefined,
    // Absent keeps what the draft remembers (constitution art. III).
    ...(defaultReasons ? { defaultReasons } : {}),
  });
  return data;
}

export async function confirmDraft(
  id: string,
  expectedVersion: number,
): Promise<SubmissionResponse> {
  const { data } = await apiClient.post<SubmissionResponse>(`/stock/drafts/${id}/confirm`, {
    expectedVersion,
  });
  return data;
}

export async function deleteDraft(id: string): Promise<void> {
  await apiClient.delete(`/stock/drafts/${id}`);
}

/** A storage location the operator may file a report against (caiet UC50 step 3). */
export interface ReportingLocation {
  id: string;
  name: string;
  internalCode: string;
}

/**
 * What the active Registrar may report for: its locations, and its type, which
 * decides the reasons its reports may carry (UC50-15). The route always names one
 * of the six (docs/shape-ops-registrar-kind-required.md §Decision 5); null is an
 * answer this bundle does not know a type in, and no reason is offered for it.
 */
export interface ReportingLocations {
  locations: ReportingLocation[];
  /**
   * The caller's own locations that are blocked or inactive. Never offered for a new
   * report; named so a draft bound to one shows its location and its state (BUG-1-088).
   */
  inactiveLocations: ReportingLocation[];
  registrarKind: RegistrarKind | null;
}

export async function listReportingLocations(): Promise<ReportingLocations> {
  const { data } = await apiClient.get<{
    locations?: ReportingLocation[];
    inactiveLocations?: ReportingLocation[];
    registrarKind?: string | null;
  }>('/stock/locations');
  return {
    locations: data.locations ?? [],
    inactiveLocations: data.inactiveLocations ?? [],
    registrarKind: knownRegistrarKind(data.registrarKind),
  };
}
